<div class="license-upload-sheet combined">
  <!-- Full-Screen Processing Overlay -->
  @if (frontImageFile && backImageFile && isExtracting && currentState !== UploadState.FRONT_CROPPING && currentState !== UploadState.BACK_CROPPING) {
    <div class="processing-overlay">
      <div class="processing-card">
        <mat-spinner diameter="60" class="large-spinner"></mat-spinner>
        <h2 class="processing-title">Reading your license...</h2>
        <p class="processing-subtitle">Please wait, this may take a few seconds.</p>
        <div class="progress-bar-container">
          <div class="progress-bar"></div>
        </div>
      </div>
    </div>
  }

  <!-- Header -->
  <div class="sheet-header">
    <button mat-icon-button (click)="onCancel()" class="close-button">
      <mat-icon>close</mat-icon>
    </button>
    <h2 class="sheet-title">Upload License Images</h2>
    <div class="spacer"></div>
  </div>

  <!-- Content Area -->
  <div class="sheet-content">
    
    <!-- FRONT LICENSE SECTION -->
    <div class="license-section" [class.section-disabled]="currentState === UploadState.BACK_CROPPING">
      <div class="section-header">
        <h3>Front License</h3>
        @if (frontImageFile) {
          <mat-icon class="status-icon primary">check_circle</mat-icon>
        }
      </div>

      <!-- Front Upload State -->
      @if (currentState === UploadState.FRONT_UPLOAD) {
        <div class="upload-area">
          <div class="upload-placeholder">
            <img 
              src="assets/images/dl-front.png" 
              alt="Front license placeholder"
              class="placeholder-image"
            />
          </div>
          <div class="upload-buttons">
            <button mat-raised-button (click)="openFrontCamera()" class="upload-btn camera-btn" data-testid="front-camera-btn">
              <mat-icon>photo_camera</mat-icon>
              Camera
            </button>
            <button mat-raised-button (click)="openFrontGallery()" class="upload-btn gallery-btn" data-testid="front-gallery-btn">
              <mat-icon>photo_library</mat-icon>
              Gallery
            </button>
          </div>
        </div>
      }

      <!-- Front Cropping State -->
      @if (currentState === UploadState.FRONT_CROPPING) {
        <div class="crop-area">
          @if (frontLoading) {
            <div class="loading-overlay">
              <mat-spinner diameter="40"></mat-spinner>
              <p>Loading image...</p>
            </div>
          }
          
          <div class="cropper-container" [class.cropper-hidden]="frontLoading">
            <image-cropper
              [imageChangedEvent]="frontImageChangedEvent"
              [maintainAspectRatio]="false"
              [imageQuality]="100"
              cropperMinWidth="100"
              cropperMinHeight="100"
              [resizeToWidth]="800"
              [roundCropper]="false"
              [canvasRotation]="canvasRotation"
              [transform]="transform"
              [format]="frontImageFormat"
              [onlyScaleDown]="true"
              [alignImage]="'center'"
              (imageCropped)="onFrontImageCropped($event)"
              (imageLoaded)="onFrontImageLoaded()"
              (loadImageFailed)="onFrontLoadImageFailed()"
            ></image-cropper>
          </div>
        </div>
      }

      <!-- Front Image Preview (Simple, Full-Width) -->
      @if (frontImageFile && currentState !== UploadState.FRONT_UPLOAD && currentState !== UploadState.FRONT_CROPPING) {
        <div class="image-preview-simple" [class.image-preview-compact]="currentState === UploadState.BACK_CROPPING">
          <img [src]="frontImageUrl" alt="Front license" />
        </div>
      }
    </div>

    <!-- Divider -->
    <mat-divider class="section-divider"></mat-divider>

    <!-- BACK LICENSE SECTION -->
    <div class="license-section" [class.section-disabled]="!isBackEnabled || currentState === UploadState.FRONT_CROPPING">
      <div class="section-header">
        <h3>Back License</h3>
        @if (backImageFile) {
          <mat-icon class="status-icon primary">check_circle</mat-icon>
        }
        @if (!isBackEnabled) {
          <span class="disabled-text">(Upload front first)</span>
        }
      </div>

      <!-- Back Upload State -->
      @if ((currentState === UploadState.EXTRACTING || currentState === UploadState.BACK_UPLOAD) && !backImageFile && isBackEnabled) {
        <div class="upload-area">
          <div class="upload-placeholder">
            <img 
              src="assets/images/dl-back.png" 
              alt="Back license placeholder"
              class="placeholder-image"
            />
          </div>
          <div class="upload-buttons">
            <button mat-raised-button (click)="openBackCamera()" class="upload-btn camera-btn" [disabled]="!isBackEnabled"
              data-testid="back-camera-btn">
              <mat-icon>photo_camera</mat-icon>
              Camera
            </button>
            <button mat-raised-button (click)="openBackGallery()" class="upload-btn gallery-btn" [disabled]="!isBackEnabled"
              data-testid="back-gallery-btn">
              <mat-icon>photo_library</mat-icon>
              Gallery
            </button>
          </div>
        </div>
      }

      <!-- Back Cropping State -->
      @if (currentState === UploadState.BACK_CROPPING) {
        <div class="crop-area">
          @if (backLoading) {
            <div class="loading-overlay">
              <mat-spinner diameter="40"></mat-spinner>
              <p>Loading image...</p>
            </div>
          }
          
          <div class="cropper-container" [class.cropper-hidden]="backLoading">
            <image-cropper
              [imageChangedEvent]="backImageChangedEvent"
              [maintainAspectRatio]="false"
              [imageQuality]="100"
              cropperMinWidth="100"
              cropperMinHeight="100"
              [resizeToWidth]="800"
              [roundCropper]="false"
              [canvasRotation]="canvasRotation"
              [transform]="transform"
              [format]="backImageFormat"
              [onlyScaleDown]="true"
              [alignImage]="'center'"
              (imageCropped)="onBackImageCropped($event)"
              (imageLoaded)="onBackImageLoaded()"
              (loadImageFailed)="onBackLoadImageFailed()"
            ></image-cropper>
          </div>
        </div>
      }

      <!-- Back Image Preview (Simple, Full-Width) -->
      @if (backImageFile && currentState !== UploadState.BACK_UPLOAD && currentState !== UploadState.BACK_CROPPING) {
        <div class="image-preview-simple">
          <img [src]="backImageUrl" alt="Back license" />
        </div>
      }

      <!-- Disabled State Placeholder -->
      @if (!isBackEnabled) {
        <div class="upload-area disabled">
          <div class="upload-placeholder">
            <img 
              src="assets/images/dl-back.png" 
              alt="Back license placeholder"
              class="placeholder-image grayscale"
            />
          </div>
        </div>
      }
    </div>
  </div>

  <!-- Info Message Section -->
  @if (frontImageFile && backImageFile && extractedData && !isExtracting && currentState !== UploadState.FRONT_CROPPING && currentState !== UploadState.BACK_CROPPING) {
    <div class="info-message" data-testid="license-extraction-success">
      <mat-icon class="info-icon">check_circle</mat-icon>
      <div class="message-text">
        <strong>License details extracted automatically.</strong>
        <span>Review the form and click <strong>Save</strong>.</span>
      </div>
    </div>
  }
  
  <!-- Extraction Error Message -->
  @if (frontImageFile && backImageFile && !extractedData && !isExtracting && extractionError && currentState !== UploadState.FRONT_CROPPING && currentState !== UploadState.BACK_CROPPING) {
    <div class="info-message error" data-testid="license-extraction-failed">
      <mat-icon class="info-icon error">error_outline</mat-icon>
      <div class="message-text">
        <strong>Unable to extract license details.</strong>
        <span>Please fill in the form manually.</span>
      </div>
    </div>
  }

  <!-- Footer with Continue Button -->
  @if (frontImageFile && backImageFile && currentState !== UploadState.FRONT_CROPPING && currentState !== UploadState.BACK_CROPPING) {
    <div class="sheet-footer">
      <button 
        mat-raised-button 
        color="primary" 
        class="continue-button"
        (click)="onContinue()"
        [disabled]="isExtracting"
        data-testid="license-upload-continue-btn"
      >
        @if (isExtracting) {
          <mat-spinner diameter="20" class="button-spinner"></mat-spinner>
          <span>Processing...</span>
        } @else if (extractedData) {
          <span>Continue</span>
        } @else {
          <span>Continue</span>
        }
      </button>
    </div>
  }

  <!-- Pinned footer for crop confirm (always visible on mobile) -->
  @if (currentState === UploadState.FRONT_CROPPING || currentState === UploadState.BACK_CROPPING) {
    <div class="sheet-footer crop-footer">
      <div class="crop-controls-actions">
        <div class="crop-controls">
          <button
            mat-icon-button
            (click)="rotateLeft()"
            [disabled]="currentState === UploadState.FRONT_CROPPING ? frontLoading : backLoading"
            matTooltip="Rotate Left"
            data-testid="rotateLeft-btn"
          >
            <mat-icon>rotate_left</mat-icon>
          </button>
          <button
            mat-icon-button
            (click)="rotateRight()"
            [disabled]="currentState === UploadState.FRONT_CROPPING ? frontLoading : backLoading"
            matTooltip="Rotate Right"
            data-testid="rotateRight-btn"
          >
            <mat-icon>rotate_right</mat-icon>
          </button>
        </div>
        <div class="action-buttons">
          @if (currentState === UploadState.FRONT_CROPPING) {
            <button
              mat-raised-button
              color="primary"
              class="confirm-button"
              (click)="onConfirmFront()"
              [disabled]="frontLoading || !frontImageFile"
              data-testid="confirm-crop-btn"
            >
              <mat-icon>check</mat-icon>
              Confirm
            </button>
          } @else {
            <button
              mat-raised-button
              color="primary"
              class="confirm-button"
              (click)="onConfirmBack()"
              [disabled]="backLoading || !backImageFile"
              data-testid="confirm-crop-btn"
            >
              <mat-icon>check</mat-icon>
              Confirm
            </button>
          }
        </div>
      </div>
    </div>
  }

  <!-- Hidden file inputs -->
  <input 
    #frontCameraInput
    type="file" 
    hidden 
    (change)="onProcessFrontImage($event)" 
    [accept]="acceptTypesPicture"
    capture="environment"
  />
  <input 
    #frontGalleryInput
    type="file" 
    hidden 
    (change)="onProcessFrontImage($event)" 
    [accept]="acceptTypesPicture"
  />
  <input 
    #backCameraInput
    type="file" 
    hidden 
    (change)="onProcessBackImage($event)" 
    [accept]="acceptTypesPicture"
    capture="environment"
  />
  <input 
    #backGalleryInput
    type="file" 
    hidden 
    (change)="onProcessBackImage($event)" 
    [accept]="acceptTypesPicture"
  />
</div>
